import React, { useCallback, useMemo, useRef, useState } from "react";
import { ColDef } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import Grid from "@/lib/organisms/Grid";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { EditBusinessFeeDialog } from "@/lib/shadcn/components/edit-business-fee-dialog";
import {
    MissingBusinessFeesBanner,
    type MissingBusinessFeeApartment,
} from "@/lib/shadcn/components/missing-business-fees-banner";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import { dateToString } from "@/utils/periods";
import { parseIsoDateOnly } from "@/utils/dates";
import { utcDateFilterParams } from "@/utils/grid";
import {
    BusinessFee,
    useActiveApartmentEntities,
    useGetBusinessFeeReadinessQuery,
    useGetCurrentBusinessFeesQuery,
} from "@/store/slices/apartments";

/**
 * The monthly business fee each apartment is currently charged
 * (`manual-business-fee/FR-014`), with the apartments that have an operational
 * month no fee covers surfaced by a banner rather than by an empty row (FR-020,
 * FR-021). The grid shows only the CURRENT fee, so a gap in an earlier month is
 * invisible in it — including for an apartment that has a fee today.
 */

/**
 * The amount arrives as a `decimal.Decimal`, which marshals as a QUOTED string
 * ("25.00"). Coerced once at the value boundary so the column is a real number
 * for `agNumberColumnFilter` and for the euro formatter.
 */
const toNumber = (value: number | string | undefined): number =>
    typeof value === "number" ? value : parseFloat(value ?? "") || 0;

/**
 * Money, NOT a ratio. The payout- and management-fee-ratio grids format their
 * value as `(value * 100).toFixed(0)%`; that formatter copied onto this column
 * renders a €25.00 fee as "2500%".
 */
const formatEuros = (value: number): string => `€${value.toFixed(2)}`;

interface BusinessFeeActionsRendererProps extends CustomCellRendererProps<BusinessFee> {
    apartmentNameOf: (apartmentId: number) => string;
    onEdit: (fee: BusinessFee) => void;
}

const BusinessFeeActionsRenderer: React.FC<BusinessFeeActionsRendererProps> = (params) => {
    const fee = params.data;
    if (!fee) {
        return null;
    }
    return (
        <div className="flex gap-3">
            <button
                type="button"
                // Named per apartment: every row offers the same icon, so a bare
                // "Edit" would leave a screen-reader user with a list of
                // indistinguishable controls (WCAG 2.4.6).
                aria-label={`Edit business fee for ${params.apartmentNameOf(fee.apartmentId)}`}
                onClick={() => params.onEdit(fee)}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "inherit" }}
                className="text-info-emphasis"
            >
                <FontAwesomeIcon icon={faEdit} />
            </button>
        </div>
    );
};

function buildBusinessFeeColumnDefinitions(deps: {
    apartmentNameOf: (apartmentId: number) => string;
    onEdit: (fee: BusinessFee) => void;
}): ColDef[] {
    const { apartmentNameOf, onEdit } = deps;
    return [
        {
            field: "apartmentId",
            headerName: "Apartment",
            valueGetter: (params) => (params.data ? apartmentNameOf(params.data.apartmentId) : ""),
            width: 300,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        {
            field: "amount",
            headerName: "Monthly Fee",
            valueGetter: (params) => toNumber(params.data?.amount),
            valueFormatter: (params) => formatEuros(toNumber(params.value)),
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 200,
        },
        {
            field: "effectiveFrom",
            headerName: "Effective From",
            // The cell value is a UTC-midnight `Date` because that is what
            // `utcDateFilterParams` compares; the label is formatted from the raw
            // ISO string instead, via a local-calendar parse.
            valueGetter: (params) => (params.data?.effectiveFrom ? new Date(params.data.effectiveFrom) : null),
            valueFormatter: (params) => dateToString(parseIsoDateOnly(params.data?.effectiveFrom)),
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            width: 200,
        },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: BusinessFeeActionsRenderer,
            cellRendererParams: { apartmentNameOf, onEdit },
            filter: false,
            flex: 1,
        },
    ];
}

const BusinessFeesPage: React.FC = () => {
    const apartmentEntities = useActiveApartmentEntities();
    const { predicates } = useApartmentFilters();

    // Where focus goes when the dialog closes and the control that opened it no
    // longer exists. Every business-fee mutation invalidates "Apartments", which
    // both the grid and the banner re-read, so the triggering row/banner entry is
    // routinely destroyed by the save itself — see `restoreFocusRef` on the dialog.
    const pageRef = useRef<HTMLDivElement>(null);

    const { data: fees = [] } = useGetCurrentBusinessFeesQuery();
    const { data: uncovered = [] } = useGetBusinessFeeReadinessQuery();

    // What the dialog acts on — the apartment, and the month to open on.
    // `undefined` means the dialog is closed. A banner entry carries the month its
    // apartment is uncovered from; a grid row carries none, and the dialog falls
    // back to the current month.
    const [dialogTarget, setDialogTarget] = useState<
        { apartmentId: number; effectiveFrom?: string } | undefined
    >(undefined);

    const apartmentNameOf = useCallback(
        (apartmentId: number) => apartmentEntities[apartmentId]?.name ?? "",
        [apartmentEntities],
    );

    const openDialogFor = useCallback(
        (apartmentId: number, firstUncoveredMonth: string) =>
            setDialogTarget({ apartmentId, effectiveFrom: firstUncoveredMonth }),
        [],
    );
    const editFee = useCallback((fee: BusinessFee) => setDialogTarget({ apartmentId: fee.apartmentId }), []);
    const closeDialog = useCallback(() => setDialogTarget(undefined), []);

    const filteredFees = useMemo(() => {
        if (predicates.length === 0) {
            return fees;
        }
        return fees.filter((fee) => {
            const apartment = apartmentEntities[fee.apartmentId];
            if (!apartment) {
                return false;
            }
            return predicates.every((predicate) => predicate(apartment));
        });
    }, [fees, predicates, apartmentEntities]);

    const missingApartments: MissingBusinessFeeApartment[] = useMemo(
        () =>
            uncovered
                .map((entry) => ({
                    id: entry.apartmentId,
                    name: apartmentEntities[entry.apartmentId]?.name,
                    firstUncoveredMonth: entry.firstUncoveredMonth,
                }))
                .filter((apartment): apartment is MissingBusinessFeeApartment => Boolean(apartment.name)),
        [uncovered, apartmentEntities],
    );

    const columnDefinitions = useMemo(
        () => buildBusinessFeeColumnDefinitions({ apartmentNameOf, onEdit: editFee }),
        [apartmentNameOf, editFee],
    );

    return (
        <div
            ref={pageRef}
            data-testid="business-fees-page"
            className="flex flex-col gap-2"
            // Programmatically focusable only (never in the tab order), and a NAMED
            // region rather than a bare div — an `aria-label` on a generic element
            // is ignored, so landing here after the dialog closes would announce
            // nothing at all instead of telling the operator where they now are.
            tabIndex={-1}
            role="region"
            aria-label="Business fees"
        >
            <ApartmentFilterBar />
            <MissingBusinessFeesBanner apartments={missingApartments} onAdd={openDialogFor} />
            {/* The dialog owns its own history query and mutations; the mutations
                invalidate "Apartments", so the grid and the banner re-read on their
                own and the page hands over nothing but which apartment to act on. */}
            <EditBusinessFeeDialog
                open={dialogTarget !== undefined}
                onClose={closeDialog}
                restoreFocusRef={pageRef}
                apartmentId={dialogTarget?.apartmentId ?? 0}
                apartmentName={apartmentNameOf(dialogTarget?.apartmentId ?? 0)}
                initialEffectiveFrom={dialogTarget?.effectiveFrom}
            />
            <Grid<BusinessFee>
                columnDefs={columnDefinitions}
                gridName="Current Business Fees"
                initialRowData={filteredFees}
                canExport={false}
                // One current fee per apartment, so the apartment IS the row.
                getRowId={(params) => params.data.apartmentId.toString()}
            />
        </div>
    );
};

export default BusinessFeesPage;
